import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { clearSession, getUser } from "@/lib/api";
import { AUTH } from "@/constants/testIds";
import { useSidebar } from "./SidebarContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Bell, LogOut, User, ChevronDown, Menu, PanelLeftClose } from "lucide-react";

const titles = {
  "/": "Dashboard",
  "/sync": "Sync Monitor",
  "/stock": "Stock Monitoring",
  "/products": "Product Master",
  "/wings-api-spec": "Wings API Specification",
  "/branches": "Branch Settings",
  "/backups": "Database Backups",
  "/production/units": "Production Units",
  "/production/raw-materials": "Raw Materials",
  "/production/rm-vendors": "Raw Material Vendors",
  "/production/workers": "Workers",
  "/production/rm-purchases": "RM Purchases (GRN)",
  "/production/rm-stocks": "Raw Material Stocks",
  "/production/worker-ledger": "Worker Ledger",
  "/production/vendor-ledger": "Vendor Ledger",
  "/production/rm-purchase-orders": "Purchase Orders",
  "/production/warehouses": "Warehouses",
  "/production/fabrications": "L1 Fabrication",
  "/production/monitor": "Production Monitor",
  "/production/platings": "L2 Plating",
  "/production/quality-control": "Quality Control",
  "/production/warehouse-stocks": "Warehouse Stock",
  "/production/taxes": "Product Taxes",
  "/sales/business-settings": "Business Settings",
  "/sales/branches": "Branches / Franchises",
};

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();
  const { collapsed, hidden, toggleHidden } = useSidebar();
  const leftPad = hidden ? "pl-4" : collapsed ? "pl-16" : "pl-64";

  const getTitle = () => {
    if (location.pathname.startsWith("/products/") && location.pathname !== "/products") return "Product Detail";
    return titles[location.pathname] || "Thara Gold";
  };

  const handleLogout = () => {
    clearSession();
    navigate("/login", { replace: true });
  };

  return (
    <header className={`h-16 w-full fixed top-0 z-20 flex items-center justify-between px-8 bg-white/80 backdrop-blur-md border-b border-gray-200 transition-[padding] duration-200 ${leftPad}`}>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={toggleHidden}
          className="h-9 w-9 rounded-md hover:bg-gray-100 border border-gray-200 flex items-center justify-center transition-colors text-gray-600 hover:text-[#9B1C1C]"
          data-testid="sidebar-hide-toggle"
          title={hidden ? "Show sidebar" : "Hide sidebar (full-width view)"}
          aria-label={hidden ? "Show sidebar" : "Hide sidebar"}
        >
          {hidden ? <Menu className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
        </button>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">
            Thara Gold / {new Intl.DateTimeFormat("en-IN", { timeZone: "Asia/Kolkata", weekday: "long", day: "numeric", month: "short" }).format(new Date())}
          </p>
          <h1 className="font-heading text-lg font-semibold text-gray-900">{getTitle()}</h1>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button className="relative h-9 w-9 rounded-md hover:bg-gray-100 flex items-center justify-center transition-colors" data-testid="header-notifications">
          <Bell className="h-4 w-4 text-gray-600" strokeWidth={1.75} />
          <span className="absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full bg-[#9B1C1C]" />
        </button>

        <div className="h-6 w-px bg-gray-200" />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-9 px-2 gap-2 hover:bg-gray-100" data-testid="header-user-menu">
              <div className="h-7 w-7 rounded-full bg-gradient-to-br from-[#9B1C1C] to-[#7F1D1D] text-white text-xs font-semibold flex items-center justify-center">
                {user?.name?.charAt(0) || "A"}
              </div>
              <div className="text-left">
                <p className="text-xs font-semibold text-gray-900 leading-tight">{user?.name || "Admin"}</p>
                <p className="text-[10px] text-gray-500 leading-tight">{user?.role || "Administrator"}</p>
              </div>
              <ChevronDown className="h-3 w-3 text-gray-400" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <p className="font-semibold">{user?.name || "Admin"}</p>
              <p className="text-xs text-gray-500 font-normal">{user?.email}</p>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem>
              <User className="h-4 w-4 mr-2" /> Profile
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleLogout}
              data-testid={AUTH.logoutButton}
              className="text-red-700 focus:text-red-700"
            >
              <LogOut className="h-4 w-4 mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
